import { ActionIcon, Box, Button, Group, Menu, Stack, Text, TextInput } from "@mantine/core";
import type { DataTableColumn } from "mantine-datatable";
import { useEffect, useState } from "react";
import { coreApi } from "../../utils/CoreAPI";
import BasePage from "../BasePage";
import {
    IconCheck,
    IconCircleCheckFilled,
    IconClockX,
    IconCopy,
    IconDots,
    IconPencil,
    IconPlus,
    IconTrash,
    IconX,
    IconXboxXFilled,
} from "@tabler/icons-react";
import { TableOverlay } from "../../components/LoadingOverlay";
import ResponsiveTable from "../../components/ResponsiveTable";
import APIKey from "../../interfaces/APIKey";
import { closeAllDialogs, openDialogBox } from "../../components/DialogBox";
import { DateTimePicker } from "@mantine/dates";
import dayjs from "dayjs";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";

const NewApiKeyForm = () => {
    const form = useForm({
        mode: 'uncontrolled',
        initialValues: {
            name: '',
            expiry_date: null,
        },
        validate: {
            name: (value) => {
                return value.length < 1 ? "Key name is too short" : null
            },
        }
    });

    const copyKey = async (key: string) => {
        try {
            await navigator.clipboard.writeText(key);

            notifications.show({
                title: "Key Copied",
                message: "The key has been copied successfully.",
                withBorder: true,
                withCloseButton: true,
                icon: <IconCheck size={"1.125rem"} />,
                color: "teal"
            });
        } catch (err) {
            notifications.show({
                title: "Copying Failed",
                message: "There was an error copying the key, please copy it manually.",
                withBorder: true,
                withCloseButton: true,
                autoClose: false,
                icon: <IconX size={"1.125rem"} />,
                color: "red"
            });

            console.error(err);
        }
    };

    const handleApiKeyCreation = async (data: any) => {
        const response = await coreApi.post('/admin/api-keys', data);

        closeAllDialogs();

        openDialogBox({
            title: `Your key '${response.data['name']}' has been created`, children: (
                <Stack>
                    <Text fz={14} c="var(--text-dimmed)">
                        Please copy the key now as the key will not be visible again.
                    </Text>

                    <Text>{response.data['key']}</Text>

                    <Group grow>
                        <Button onClick={closeAllDialogs}>Close</Button>
                        <Button
                            className="unify-btn-primary"
                            leftSection={<IconCopy size={"1.125rem"} />}
                            onClick={() => { copyKey(response.data['key']) }}
                        >
                            Copy key
                        </Button>
                    </Group>
                </Stack>
            )
        });
    };

    return (
        <form onSubmit={form.onSubmit((values) => handleApiKeyCreation(values))}>
            <Stack>
                <TextInput
                    withAsterisk
                    label="Name"
                    key={form.key('name')}
                    {...form.getInputProps('name')}
                />

                <DateTimePicker
                    clearable
                    label="Expiration date"
                    placeholder="Leave empty to never expire the key"
                    valueFormat="DD/MM/YYYY hh:mm A"
                    key={form.key('expiry_date')}
                    {...form.getInputProps('expiry_date')}
                />

                <Group justify="right" gap={6}>
                    <Button onClick={closeAllDialogs} radius={12} h={34}>Cancel</Button>
                    <Button className="unify-btn-primary" type="submit" radius={12} h={34}>Create</Button>
                </Group>
            </Stack>
        </form>
    );
};

const EditKeyForm = ({ apiKey }: { apiKey: APIKey }) => {
    const form = useForm({
        mode: 'uncontrolled',
        initialValues: {
            name: apiKey.name,
            expiry_date: apiKey.expiry_date ? new Date(apiKey.expiry_date) : null
        },
        validate: {
            name: (value) => {
                return value.length < 1 ? "Key name is too short" : null
            },
        },
        transformValues: (values) => ({
            ...values,
            expiry_date: values.expiry_date ? dayjs(values.expiry_date).toISOString() : null
        })
    });

    const handleKeyEditing = async (data: any) => {
        await coreApi.patch(`/admin/api-keys?id=${apiKey.id}`, data);
        window.location.reload();
    };

    return (
        <form onSubmit={form.onSubmit((values) => handleKeyEditing(values))}>
            <Stack>
                <TextInput
                    withAsterisk
                    label="Name"
                    key={form.key('name')}
                    {...form.getInputProps('name')}
                />

                <DateTimePicker
                    clearable
                    label="Expiry Date"
                    placeholder="Leave empty to never expire the key"
                    valueFormat="DD/MM/YYYY, hh:mm A"
                    key={form.key('expiry_date')}
                    {...form.getInputProps('expiry_date')}
                />

                <Group justify="right" gap={6}>
                    <Button onClick={closeAllDialogs} radius={12} h={34}>Cancel</Button>
                    <Button className="unify-btn-primary" type="submit" radius={12} h={34}>Save changes</Button>
                </Group>
            </Stack>
        </form>
    );
};

const RevokeKeyForm = ({ apiKey }: { apiKey: APIKey }) => {
    const form = useForm({
        mode: 'uncontrolled',
        initialValues: {
            name: '',
        },
        validate: {
            name: (value) => {
                return value !== apiKey.name ? "Key name does not match" : null
            },
        }
    });

    const handleKeyRevocation = async (data: any) => {
        await coreApi.patch(`/admin/api-keys?id=${apiKey.id}`, { 'revoked': true });
        window.location.reload();
    };

    return (
        <form onSubmit={form.onSubmit((values) => handleKeyRevocation(values))}>
            <Stack>
                <Text fz={14} c="var(--text-dimmed)">
                    Type the name of the key as shown '{apiKey.name}' to confirm.
                </Text>

                <TextInput
                    withAsterisk
                    label="Name"
                    key={form.key('name')}
                    {...form.getInputProps('name')}
                />

                <Group justify="right" gap={6}>
                    <Button onClick={closeAllDialogs} radius={12} h={34}>Cancel</Button>
                    <Button className="unify-btn-primary" type="submit" radius={12} h={34}>Revoke</Button>
                </Group>
            </Stack>
        </form>
    );
};

const DeleteKeyForm = ({ apiKey }: { apiKey: APIKey }) => {
    const form = useForm({
        mode: 'uncontrolled',
        initialValues: {
            name: '',
        },
        validate: {
            name: (value) => {
                return value !== apiKey.name ? "Key name does not match" : null
            },
        }
    });

    const handleKeyDeletion = async () => {
        await coreApi.delete(`/admin/api-keys?id=${apiKey.id}`);
        window.location.reload();
    };

    return (
        <form onSubmit={form.onSubmit(handleKeyDeletion)}>
            <Stack>
                <Text fz={14} c="var(--text-dimmed)">
                    Type the name of the key as shown '{apiKey.name}' to confirm.
                </Text>

                <TextInput
                    withAsterisk
                    label="Name"
                    key={form.key('name')}
                    {...form.getInputProps('name')}
                />

                <Group justify="right" gap={6}>
                    <Button onClick={closeAllDialogs} radius={12} h={34}>Cancel</Button>
                    <Button className="unify-btn-primary" type="submit" radius={12} h={34}>Delete</Button>
                </Group>
            </Stack>
        </form>
    );
};

function ActionMenu(record: APIKey) {
    return (
        <Box>
            <Menu width={200}>
                <Menu.Target>
                    <ActionIcon className="unify-action-icon-subtle">
                        <IconDots size="1.125rem" />
                    </ActionIcon>
                </Menu.Target>

                <Menu.Dropdown>
                    <Menu.Label>API Key</Menu.Label>

                    <Menu.Item
                        leftSection={<IconPencil size="1.125rem" />}
                        onClick={() => {
                            openDialogBox({
                                title: "Edit Key",
                                children: <EditKeyForm apiKey={record} />
                            })
                        }}
                    >
                        Edit Key
                    </Menu.Item>

                    <Menu.Divider />

                    <Menu.Label>Danger Zone</Menu.Label>

                    <Menu.Item
                        color="red" leftSection={<IconX size="1.125rem" />}
                        onClick={() => {
                            openDialogBox({
                                title: "Revoke Key",
                                description: "Any third-party apps using this key will stop working. This action cannot be reversed.",
                                children: <RevokeKeyForm apiKey={record} />
                            })
                        }}
                    >
                        Revoke
                    </Menu.Item>

                    <Menu.Item
                        color="red" leftSection={<IconTrash size="1.125rem" />}
                        onClick={() => {
                            openDialogBox({
                                title: "Delete Key",
                                description: "Any third-party apps using this key will stop working. This action cannot be reversed.",
                                children: <DeleteKeyForm apiKey={record} />
                            })
                        }}
                    >
                        Delete
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>
        </Box>
    )
}

function APIKeysPage() {
    const [loading, setLoading] = useState(true);
    const [keysData, setKeysData] = useState<APIKey[]>([]);
    const [selectedRows, setSelectedRows] = useState<React.Key[]>([]);

    const columns: DataTableColumn<APIKey>[] = [
        { accessor: "name", title: "Name", sortable: true },
        { accessor: "prefix", title: "Prefix", sortable: true },
        { accessor: "hashed_key", title: "Hashed Key", sortable: true },
        {
            accessor: "created",
            title: "Created At",
            sortable: true,
            render: (record) => dayjs(record.created).format("DD/MM/YYYY, hh:mm A"),
        },
        {
            accessor: "expiry_date",
            title: "Expires At",
            sortable: true,
            render: (record) => {
                const formatted = dayjs(record.expiry_date).format("DD/MM/YYYY, hh:mm A");
                return formatted === "Invalid Date" ? "Never" : formatted;
            },
        },
        {
            accessor: "revoked",
            title: "Status",
            sortable: true,
            textAlign: "center",
            render: (record) => {
                const isExpired = record.expiry_date && new Date(record.expiry_date) < new Date();

                if (record.revoked) return <IconXboxXFilled color="red" size="1.125rem" />;
                if (isExpired) return <IconClockX color="orange" size="1.125rem" />;
                return <IconCircleCheckFilled color="green" size="1.125rem" />;
            },
        },
        {
            accessor: "actions",
            title: "",
            textAlign: "left",
            render: (record) => ActionMenu(record),
        },
    ];

    useEffect(() => {
        const getApiKeys = async () => {
            try {
                const res = await coreApi.get("/admin/api-keys");
                setKeysData(res.data);
            } finally {
                setLoading(false);
            }
        };

        getApiKeys();
    }, []);

    return (
        <BasePage
            title="API Keys"
            desc="Control who has API access to Unify, useful for third-party integrations."
            breadcrumbs={[{ label: "API Keys" }]}
            fullWidth
        >
            <TableOverlay visible={loading}>
                <Box
                    style={{
                        borderBottom: "1px solid var(--border-color)",
                    }}
                >
                    <Button
                        className="unify-btn-subtle unify-border-0"
                        leftSection={<IconPlus size="1.125rem" />}
                        onClick={() => { openDialogBox({ title: "New API Key", children: <NewApiKeyForm /> }) }}
                    >
                        New Key
                    </Button>
                </Box>

                <ResponsiveTable
                    data={keysData}
                    columns={columns}
                    gridRowId={(row) => row.id}
                    rowSelectionModel={selectedRows}
                    onSelectionChange={setSelectedRows}
                    gridSortModel={{
                        columnAccessor: "name",
                        direction: "asc",
                    }}
                    selectionColumnStyle={{ display: 'none' }}
                />
            </TableOverlay>
        </BasePage>
    );
}

export default APIKeysPage;